import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LeadStatus } from '@/types/lead';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBusinessData } from '@/hooks/useBusinessData';
import { useSidebar } from '@/context/SidebarContext';
import { getCompanyNoteTags, updateNote, deleteNote } from '@/services/leads/noteService';

// Import our components
import BusinessHeader from '@/components/business/BusinessHeader';
import TabContentSection from '@/components/business/TabContentSection';
import BusinessSidebar from '@/components/business/BusinessSidebar';
import LoadingView from '@/components/business/LoadingView';
import NotFoundView from '@/components/business/NotFoundView';
import CommunicationSidePanel from '@/components/business/communication/CommunicationSidePanel';
import CommunicationBottomSheet from '@/components/business/communication/CommunicationBottomSheet';
import MobileBottomNav from '@/components/navigation/MobileBottomNav';
import { LeadChatbot } from '@/components/business/LeadChatbot';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const BusinessDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setActiveTab: setSidebarActiveTab, setOnTabChange, setLeadName, setOnOpenCommunication } = useSidebar();
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [isCommunicationPanelCollapsed, setIsCommunicationPanelCollapsed] = useState(true);
  const [isCommunicationSheetOpen, setIsCommunicationSheetOpen] = useState(false);
  const [communicationSheetTab, setCommunicationSheetTab] = useState<'email' | 'call'>('email');
  
  const {
    lead,
    tasks,
    isLoading,
    activeTab,
    setActiveTab,
    handleToggleFavorite,
    handleStatusChange,
    handleAddNote,
    handleAddDocument,
    fetchLead,
    handleRefreshPlaceData,
    isRefreshing
  } = useBusinessData(id);

  // Sync lead data with sidebar context
  useEffect(() => {
    if (lead) {
      setLeadName(lead.name);
    }
    setSidebarActiveTab(activeTab);
    setOnTabChange(setActiveTab);
    setOnOpenCommunication(handleOpenCommunicationSheet);
  }, [lead, activeTab, setActiveTab, setSidebarActiveTab, setOnTabChange, setLeadName, setOnOpenCommunication]);

  // Initialize tab from URL hash on first load
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['overview', 'notes', 'tasks', 'contacts', 'documents', 'reviews', 'activity', 'score'];
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lead?.companyId) {
      fetchNoteTags(lead.companyId);
    }
  }, [lead?.companyId]);

  // Ensure communication panel is collapsed when page loads or lead changes
  useEffect(() => {
    setIsCommunicationPanelCollapsed(true);
  }, [id]);

  const fetchNoteTags = async (companyId: string) => {
    try {
      const tags = await getCompanyNoteTags(companyId);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Error fetching note tags:', error);
    }
  };

  const handleContactsUpdated = () => {
    // Refetch lead to get updated contacts
    fetchLead();
  };
  
  const handleDocumentDeleted = () => {
    // Refetch lead to get updated documents list
    fetchLead();
  };

  const handleOpenAddDocument = () => {
    setIsAddDocumentOpen(true);
    setActiveTab('documents');
  };

  const handleOpenCommunicationSheet = useCallback((type: 'email' | 'call') => {
    setCommunicationSheetTab(type);
    setIsCommunicationSheetOpen(true);
  }, []);

  const handleUpdateNote = async (noteId: string, updates: { content: string; tags: string[] }) => {
    try {
      await updateNote(noteId, updates);
      
      // Refresh the lead data to get the updated notes
      fetchLead();
      
      // Update available tags
      if (lead?.companyId) {
        fetchNoteTags(lead.companyId);
      }
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!id || !lead) return;
    try {
      await deleteNote(id, noteId);
      
      // Refresh the lead data to get the updated notes
      fetchLead();
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  };
  
  if (isLoading) {
    return <LoadingView />;
  }
  
  if (!lead) {
    return <NotFoundView onNavigateBack={() => navigate('/app')} />;
  }

  // Compute content wrapper class to account for communication panel
  const contentWrapperClass = cn(
    // Base styles - mobile first (full width with standard padding)
    "w-full max-w-full px-3 py-3",
    
    // Tablet (md: 768px+) - add space for collapsed comm panel
    "md:px-4 md:pr-20",
    
    // Desktop (lg: 1024px+) - content is already constrained by flex layout
    // Left nav (220px) is outside main, so we just need right padding for comm panel
    "lg:px-6",
    
    // Adjust right padding based on communication panel state
    !isMobile && isCommunicationPanelCollapsed && [
      "md:pr-24", // Collapsed panel (64px) + gap
      "lg:pr-24",
      "xl:pr-24"
    ],
    !isMobile && !isCommunicationPanelCollapsed && [
      "md:pr-[420px]", // Expanded panel width + gap
      "lg:pr-[440px]",
      "xl:pr-[460px]"
    ]
  );

  return (
    <div className={contentWrapperClass}>
      <div className="mb-4">
        <BusinessHeader 
          lead={lead} 
          onToggleFavorite={handleToggleFavorite} 
          onNavigateBack={() => navigate('/app')} 
          onRefresh={handleRefreshPlaceData}
          isRefreshing={isRefreshing}
        />
      </div>
      
      <div className="w-full relative">
        {/* Main content - always full width */}
        <div className="w-full">
          <Card className="bg-white shadow-md border-gray-200">
            <CardContent className="p-0">
              <TabContentSection
                lead={lead}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onAddNote={handleAddNote}
                onUpdateNote={handleUpdateNote}
                onDeleteNote={handleDeleteNote}
                onAddDocument={handleAddDocument}
                onContactsUpdated={handleContactsUpdated}
                onDocumentDeleted={handleDocumentDeleted}
                isAddDocumentOpen={isAddDocumentOpen}
                onAddDocumentOpenChange={setIsAddDocumentOpen}
                availableTags={availableTags}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Desktop sidebar - only shown when communication panel is closed */}
        {!isMobile && !isCommunicationPanelCollapsed && (
          <div className="hidden xl:block fixed right-6 top-32 w-[280px] max-h-[calc(100vh-9rem)] overflow-y-auto z-40">
            <Card className="bg-white shadow-lg border-gray-200 sticky top-24 rounded-lg">
              <CardContent className="p-5 space-y-4">
                <BusinessSidebar
                  lead={lead}
                  tasks={tasks}
                  setActiveTab={setActiveTab} 
                  onStatusChange={handleStatusChange}
                  onOpenAddDocument={handleOpenAddDocument}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Desktop Communication Side Panel */}
      {!isMobile && lead && (
        <CommunicationSidePanel
          leadId={lead.id}
          isCollapsed={isCommunicationPanelCollapsed}
          onToggleCollapse={() => setIsCommunicationPanelCollapsed(!isCommunicationPanelCollapsed)}
        />
      )}

      {/* Mobile Communication Bottom Sheet */}
      {isMobile && lead && (
        <CommunicationBottomSheet
          leadId={lead.id}
          isOpen={isCommunicationSheetOpen}
          onClose={() => setIsCommunicationSheetOpen(false)}
          defaultTab={communicationSheetTab}
        />
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && lead && (
        <MobileBottomNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      {/* AI Chatbot - Fixed position, always visible when lead loaded */}
      {lead && <LeadChatbot lead={lead} />}
    </div>
  );
};

export default BusinessDetails;
